/**
 * Email Sequences API - Automated multi-step email campaigns
 */
import apiClient from './index'

// =============================================================================
// TYPES
// =============================================================================

export interface EmailSequence {
  id: string
  name: string
  description?: string
  status: 'draft' | 'active' | 'paused' | 'archived'
  owner_user_id?: string
  shared_with_user_ids: string[]
  step_count: number
  send_as_reply: boolean
  stop_on_reply: boolean
  stop_on_meeting: boolean
  stop_on_bounce: boolean
  send_on_weekdays_only: boolean
  send_window_start: string
  send_window_end: string
  timezone: string
  total_enrolled: number
  total_completed: number
  total_replied: number
  total_bounced: number
  total_unsubscribed: number
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  open_rate: number
  click_rate: number
  reply_rate: number
  activated_at?: string
  paused_at?: string
  created_at: string
  updated_at: string
}

export interface SequenceStep {
  id: string
  sequence_id: string
  step_number: number
  step_type: 'email' | 'task' | 'wait'
  delay_days: number
  delay_hours: number
  delay_minutes: number
  delay_description: string
  total_delay_minutes: number
  subject_template: string
  body_template: string
  body_text_template: string
  task_title: string
  task_description: string
  task_type: string
  is_ab_test: boolean
  variant_a_subject: string
  variant_a_body: string
  variant_b_subject: string
  variant_b_body: string
  ab_test_percent: number
  send_time_preference: string
  skip_weekends: boolean
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  emails_replied: number
  emails_bounced: number
  created_at: string
  updated_at: string
}

export interface SequenceEnrollment {
  id: string
  sequence_id: string
  contact_id: string
  lead_id?: string
  status: 'active' | 'paused' | 'completed' | 'replied' | 'bounced' | 'unsubscribed' | 'cancelled'
  current_step: number
  next_step: number
  completed_steps: number[]
  next_send_at?: string
  last_sent_at?: string
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  replied: boolean
  replied_at?: string
  paused_at?: string
  paused_reason?: string
  enrolled_by_user_id?: string
  enrollment_source: string
  started_at?: string
  completed_at?: string
  created_at: string
}

export interface SequenceEmail {
  id: string
  sequence_id: string
  enrollment_id: string
  step_id: string
  contact_id: string
  subject: string
  to_email: string
  status: string
  sent_at?: string
  delivered_at?: string
  first_opened_at?: string
  last_opened_at?: string
  first_clicked_at?: string
  open_count: number
  click_count: number
  clicked_links: string[]
  replied: boolean
  replied_at?: string
  bounced: boolean
  bounce_type?: string
  bounce_reason?: string
  error_message?: string
  created_at: string
}

export interface ContactBrief {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  job_title?: string
}

export interface SequenceBrief {
  id: string
  name: string
  status: string
  step_count: number
}

export interface SequenceAnalytics {
  sequence_id: string
  name: string
  status: string
  total_enrolled: number
  total_completed: number
  total_replied: number
  total_bounced: number
  total_unsubscribed: number
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  open_rate: number
  click_rate: number
  reply_rate: number
  completion_rate: number
  bounce_rate: number
  step_count: number
  steps: StepMetrics[]
}

export interface StepMetrics {
  step_number: number
  step_type: string
  subject: string
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  emails_replied: number
  emails_bounced: number
  open_rate: number
  click_rate: number
  reply_rate: number
}

export interface TemplateVariable {
  name: string
  description: string
}

// =============================================================================
// API METHODS
// =============================================================================

export const sequencesApi = {
  // =========================================================================
  // SEQUENCES CRUD
  // =========================================================================

  /**
   * List all sequences
   */
  async listSequences(params?: {
    status?: string
    owner_id?: string
  }): Promise<EmailSequence[]> {
    const response = await apiClient.get<{ sequences: EmailSequence[] }>(
      '/sequences',
      { params }
    )
    return response.data.sequences
  },

  /**
   * Create a new sequence
   */
  async createSequence(data: {
    name: string
    description?: string
    send_as_reply?: boolean
    stop_on_reply?: boolean
    stop_on_meeting?: boolean
    stop_on_bounce?: boolean
    send_on_weekdays_only?: boolean
    send_window_start?: string
    send_window_end?: string
    timezone?: string
  }): Promise<EmailSequence> {
    const response = await apiClient.post<{ sequence: EmailSequence }>('/sequences', data)
    return response.data.sequence
  },

  /**
   * Get a sequence with its steps
   */
  async getSequence(sequenceId: string): Promise<{
    sequence: EmailSequence
    steps: SequenceStep[]
  }> {
    const response = await apiClient.get<{
      sequence: EmailSequence
      steps: SequenceStep[]
    }>(`/sequences/${sequenceId}`)
    return response.data
  },

  /**
   * Update a sequence
   */
  async updateSequence(
    sequenceId: string,
    data: Partial<EmailSequence>
  ): Promise<EmailSequence> {
    const response = await apiClient.put<{ sequence: EmailSequence }>(
      `/sequences/${sequenceId}`,
      data
    )
    return response.data.sequence
  },

  /**
   * Delete a sequence
   */
  async deleteSequence(sequenceId: string): Promise<void> {
    await apiClient.delete(`/sequences/${sequenceId}`)
  },

  /**
   * Activate a sequence
   */
  async activateSequence(sequenceId: string): Promise<EmailSequence> {
    const response = await apiClient.post<{ sequence: EmailSequence }>(
      `/sequences/${sequenceId}/activate`
    )
    return response.data.sequence
  },

  /**
   * Pause a sequence
   */
  async pauseSequence(sequenceId: string): Promise<EmailSequence> {
    const response = await apiClient.post<{ sequence: EmailSequence }>(
      `/sequences/${sequenceId}/pause`
    )
    return response.data.sequence
  },

  /**
   * Archive a sequence
   */
  async archiveSequence(sequenceId: string): Promise<EmailSequence> {
    const response = await apiClient.post<{ sequence: EmailSequence }>(
      `/sequences/${sequenceId}/archive`
    )
    return response.data.sequence
  },

  /**
   * Duplicate a sequence
   */
  async duplicateSequence(
    sequenceId: string,
    newName?: string
  ): Promise<{ sequence: EmailSequence; steps: SequenceStep[] }> {
    const response = await apiClient.post<{
      sequence: EmailSequence
      steps: SequenceStep[]
    }>(`/sequences/${sequenceId}/duplicate`, { name: newName })
    return response.data
  },

  // =========================================================================
  // STEPS
  // =========================================================================

  /**
   * Get all steps for a sequence
   */
  async getSteps(sequenceId: string): Promise<SequenceStep[]> {
    const response = await apiClient.get<{ steps: SequenceStep[] }>(
      `/sequences/${sequenceId}/steps`
    )
    return response.data.steps
  },

  /**
   * Add a step to a sequence
   */
  async addStep(
    sequenceId: string,
    data: {
      step_type?: 'email' | 'task' | 'wait'
      delay_days?: number
      delay_hours?: number
      delay_minutes?: number
      subject_template?: string
      body_template?: string
      body_text_template?: string
      task_title?: string
      task_description?: string
      task_type?: string
      send_time_preference?: string
      skip_weekends?: boolean
      is_ab_test?: boolean
      variant_a_subject?: string
      variant_a_body?: string
      variant_b_subject?: string
      variant_b_body?: string
      ab_test_percent?: number
    }
  ): Promise<SequenceStep> {
    const response = await apiClient.post<{ step: SequenceStep }>(
      `/sequences/${sequenceId}/steps`,
      data
    )
    return response.data.step
  },

  /**
   * Update a step
   */
  async updateStep(
    sequenceId: string,
    stepId: string,
    data: Partial<SequenceStep>
  ): Promise<SequenceStep> {
    const response = await apiClient.put<{ step: SequenceStep }>(
      `/sequences/${sequenceId}/steps/${stepId}`,
      data
    )
    return response.data.step
  },

  /**
   * Delete a step
   */
  async deleteStep(sequenceId: string, stepId: string): Promise<void> {
    await apiClient.delete(`/sequences/${sequenceId}/steps/${stepId}`)
  },

  /**
   * Reorder steps in a sequence
   */
  async reorderSteps(
    sequenceId: string,
    stepOrder: string[]
  ): Promise<SequenceStep[]> {
    const response = await apiClient.post<{ steps: SequenceStep[] }>(
      `/sequences/${sequenceId}/steps/reorder`,
      { step_order: stepOrder }
    )
    return response.data.steps
  },

  // =========================================================================
  // ENROLLMENTS
  // =========================================================================

  /**
   * List enrollments for a sequence
   */
  async listEnrollments(
    sequenceId: string,
    params?: { status?: string; limit?: number }
  ): Promise<Array<SequenceEnrollment & { contact?: ContactBrief }>> {
    const response = await apiClient.get<{
      enrollments: Array<SequenceEnrollment & { contact?: ContactBrief }>
    }>(`/sequences/${sequenceId}/enrollments`, { params })
    return response.data.enrollments
  },

  /**
   * Enroll contacts in a sequence
   */
  async enrollContacts(
    sequenceId: string,
    data: {
      contact_id?: string
      contact_ids?: string[]
      personalization?: Record<string, unknown>
    }
  ): Promise<
    | { enrollment: SequenceEnrollment & { contact?: ContactBrief } }
    | {
        enrolled: number
        skipped: number
        failed: number
        errors: Array<{ contact_id: string; error: string }>
      }
  > {
    const response = await apiClient.post(`/sequences/${sequenceId}/enroll`, data)
    return response.data
  },

  /**
   * Get enrollment details
   */
  async getEnrollment(enrollmentId: string): Promise<{
    enrollment: SequenceEnrollment
    contact?: ContactBrief
    sequence?: SequenceBrief
    emails: SequenceEmail[]
  }> {
    const response = await apiClient.get<{
      enrollment: SequenceEnrollment
      contact?: ContactBrief
      sequence?: SequenceBrief
      emails: SequenceEmail[]
    }>(`/sequences/enrollments/${enrollmentId}`)
    return response.data
  },

  /**
   * Pause an enrollment
   */
  async pauseEnrollment(
    enrollmentId: string,
    reason?: string
  ): Promise<SequenceEnrollment> {
    const response = await apiClient.post<{ enrollment: SequenceEnrollment }>(
      `/sequences/enrollments/${enrollmentId}/pause`,
      { reason }
    )
    return response.data.enrollment
  },

  /**
   * Resume an enrollment
   */
  async resumeEnrollment(enrollmentId: string): Promise<SequenceEnrollment> {
    const response = await apiClient.post<{ enrollment: SequenceEnrollment }>(
      `/sequences/enrollments/${enrollmentId}/resume`
    )
    return response.data.enrollment
  },

  /**
   * Cancel/remove an enrollment
   */
  async cancelEnrollment(
    enrollmentId: string,
    reason?: string
  ): Promise<void> {
    await apiClient.delete(`/sequences/enrollments/${enrollmentId}`, {
      data: { reason },
    })
  },

  /**
   * Get sequence enrollments for a contact
   */
  async getContactSequences(contactId: string): Promise<
    Array<SequenceEnrollment & { sequence?: SequenceBrief }>
  > {
    const response = await apiClient.get<{
      enrollments: Array<SequenceEnrollment & { sequence?: SequenceBrief }>
    }>(`/contacts/${contactId}/sequences`)
    return response.data.enrollments
  },

  // =========================================================================
  // ANALYTICS
  // =========================================================================

  /**
   * Get analytics for a sequence
   */
  async getAnalytics(sequenceId: string): Promise<SequenceAnalytics> {
    const response = await apiClient.get<SequenceAnalytics>(
      `/sequences/${sequenceId}/analytics`
    )
    return response.data
  },

  // =========================================================================
  // TEMPLATE VARIABLES
  // =========================================================================

  /**
   * Get available template variables
   */
  async getTemplateVariables(): Promise<TemplateVariable[]> {
    const response = await apiClient.get<{ variables: TemplateVariable[] }>(
      '/sequences/template-variables'
    )
    return response.data.variables
  },
}

export default sequencesApi
